import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// --- Types ---
export interface SubjectMark {
  subject: string;
  marks: number;
}

export interface GosvaraSheetData {
  id?: number;
  studentId: number;
  schoolId: number;
  classId: number;
  marksheetType: string; // Changed from gosvaraType to marksheetType
  monthType: string;   // e.g. "JANUARY"
  subjectMarks: SubjectMark[];
  sessionId?: number;
}

// New interface matching the backend GosvaraRequestDto
export interface GosvaraRequestDto {
  schoolId: number;
  sessionId: number;
  classId?: number | null;
  className?: string;
  marksheetType: string;
  monthType: string;
}

export interface GosvaraGetRequest {
  schoolId: number;
  sessionId: number;
  classId?: number | null;
  className?: string;
  marksheetType: string;
  monthType: string;
}

export interface GosvaraSubjectResult {
  subject: string;
  result: Record<string, number>;
  registered: number;
  registeredMale?: number;
  registeredFemale?: number;
  appeared: number;
  appearedMale?: number;
  appearedFemale?: number;
  absent: number;
  absentMale?: number;
  absentFemale?: number;
  passed: number;
  passedMale?: number;
  passedFemale?: number;
  failed?: number;
  failedMale?: number;
  failedFemale?: number;
  passPercentage: number;
}

export interface GosvaraGetResponse {
  schoolId: number;
  classId: number;
  className?: string;
  marksheetType: string; // Changed from gosvaraType to marksheetType
  monthType: string;
  overallGradeSummary: Record<string, number>; // Added overallGradeSummary field
  subjectResults: GosvaraSubjectResult[];
}

// Response DTO for category-wise gosvara (value type in the returned map)
export interface GosvaraResponseDto {
  subjectResults: GosvaraSubjectResult[];
  overallGradeSummary: Record<string, number>;
  maleGradeSummary?: Record<string, number>;
  femaleGradeSummary?: Record<string, number>;
  marksheetType: string;
  monthType: string;
  schoolId: number;
  classId: number;
  className?: string;
  categoryStudentCount?: Record<string, Record<string, number>>;
}

// --- API URL ---
const GOSVARA_GET_URL = `${process.env.REACT_APP_API_URL}/gosvara/get`;
const GOSVARA_LIST_URL = `${process.env.REACT_APP_API_URL}/gosvara/get/schoolId/classKey/sessionId/medium`;
const GOSVARA_CATEGORY_WISE_URL = `${process.env.REACT_APP_API_URL}/gosvara/category-wise`;

const getGosvaraByClassAndSession = async (args: {
  schoolId: number;
  sessionId: number;
  medium: string;
  classId?: number;
  className?: string;
}): Promise<GosvaraResponseDto[]> => {
  const response = await axios.get(GOSVARA_LIST_URL, {
    params: {
      schoolId: args.schoolId,
      sessionId: args.sessionId,
      medium: args.medium,
      ...(args.classId != null ? { classId: args.classId } : {}),
      ...(args.className ? { className: args.className } : {}),
    },
  });
  return response.data;
};

const sumNumberMap = (a?: Record<string, number>, b?: Record<string, number>): Record<string, number> => {
  const out: Record<string, number> = { ...(a || {}) };
  for (const [k, v] of Object.entries(b || {})) {
    out[k] = (out[k] ?? 0) + (Number(v) || 0);
  }
  return out;
};

const sumNestedNumberMap = (
  a?: Record<string, Record<string, number>>,
  b?: Record<string, Record<string, number>>
): Record<string, Record<string, number>> => {
  const out: Record<string, Record<string, number>> = {};
  const keys = Array.from(new Set<string>([...Object.keys(a || {}), ...Object.keys(b || {})]));
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    out[key] = sumNumberMap(a?.[key], b?.[key]);
  }
  return out;
};

export const combineGosvaraDtos = (dtos: GosvaraResponseDto[], combinedClassName?: string): GosvaraResponseDto[] => {
  const byKey = new Map<string, GosvaraResponseDto[]>();
  for (const dto of dtos || []) {
    const k = `${dto.marksheetType}__${dto.monthType}`;
    const arr = byKey.get(k) || [];
    arr.push(dto);
    byKey.set(k, arr);
  }

  const combined: GosvaraResponseDto[] = [];
  const groups = Array.from(byKey.values());
  for (let gi = 0; gi < groups.length; gi++) {
    const group = groups[gi];
    const first = group[0];
    const subjectNames = new Set<string>();
    for (const g of group) {
      (g.subjectResults || []).forEach((sr: GosvaraSubjectResult) => subjectNames.add(sr.subject));
    }

    const subjectResults: GosvaraSubjectResult[] = Array.from(subjectNames).map((subject: string) => {
      const rows = group
        .flatMap((g) => g.subjectResults || [])
        .filter((sr) => sr.subject === subject);

      const merged: any = {
        subject,
        result: {},
        registered: 0,
        appeared: 0,
        absent: 0,
        passed: 0,
        failed: 0,
        passPercentage: 0,
      };

      for (const r of rows) {
        merged.result = sumNumberMap(merged.result, r.result as any);
        merged.registered += Number((r as any).registered) || 0;
        merged.registeredMale = (Number(merged.registeredMale) || 0) + (Number((r as any).registeredMale) || 0);
        merged.registeredFemale = (Number(merged.registeredFemale) || 0) + (Number((r as any).registeredFemale) || 0);
        merged.appeared += Number((r as any).appeared) || 0;
        merged.appearedMale = (Number(merged.appearedMale) || 0) + (Number((r as any).appearedMale) || 0);
        merged.appearedFemale = (Number(merged.appearedFemale) || 0) + (Number((r as any).appearedFemale) || 0);
        merged.absent += Number((r as any).absent) || 0;
        merged.absentMale = (Number(merged.absentMale) || 0) + (Number((r as any).absentMale) || 0);
        merged.absentFemale = (Number(merged.absentFemale) || 0) + (Number((r as any).absentFemale) || 0);
        merged.passed += Number((r as any).passed) || 0;
        merged.passedMale = (Number(merged.passedMale) || 0) + (Number((r as any).passedMale) || 0);
        merged.passedFemale = (Number(merged.passedFemale) || 0) + (Number((r as any).passedFemale) || 0);
        merged.failed = (Number(merged.failed) || 0) + (Number((r as any).failed) || 0);
        merged.failedMale = (Number(merged.failedMale) || 0) + (Number((r as any).failedMale) || 0);
        merged.failedFemale = (Number(merged.failedFemale) || 0) + (Number((r as any).failedFemale) || 0);
      }

      const appeared = Number(merged.appeared) || 0;
      merged.passPercentage = appeared > 0 ? Math.round((Number(merged.passed || 0) / appeared) * 100) : 0;
      return merged as GosvaraSubjectResult;
    });

    // Keep TOTAL row last if present
    const nonTotal = subjectResults.filter((sr) => sr.subject !== "TOTAL");
    const totalRow = subjectResults.find((sr) => sr.subject === "TOTAL");
    const ordered = totalRow ? [...nonTotal, totalRow] : nonTotal;

    combined.push({
      schoolId: first.schoolId,
      classId: first.classId,
      className: combinedClassName ?? first.className,
      marksheetType: first.marksheetType,
      monthType: first.monthType,
      overallGradeSummary: group.reduce<Record<string, number>>((acc, g: GosvaraResponseDto) => sumNumberMap(acc, g.overallGradeSummary), {}),
      maleGradeSummary: group.reduce<Record<string, number>>((acc, g: GosvaraResponseDto) => sumNumberMap(acc, g.maleGradeSummary), {}),
      femaleGradeSummary: group.reduce<Record<string, number>>((acc, g: GosvaraResponseDto) => sumNumberMap(acc, g.femaleGradeSummary), {}),
      categoryStudentCount: group.reduce<Record<string, Record<string, number>>>(
        (acc, g: GosvaraResponseDto) => sumNestedNumberMap(acc, g.categoryStudentCount),
        {}
      ),
      subjectResults: ordered,
    });
  }

  return combined;
};

const getMonthlyGosvara = async (payload: GosvaraGetRequest): Promise<GosvaraGetResponse> => {
  const response = await axios.post(GOSVARA_GET_URL, payload);
  return response.data;
};

const getCategoryWiseGosvara = async (
  payload: GosvaraRequestDto,
  medium: string = 'ALL'
): Promise<Record<string, GosvaraResponseDto>> => {
  const response = await axios.post(GOSVARA_CATEGORY_WISE_URL, payload, {
    params: { medium },
  });
  return response.data;
};

// --- Hook ---
export function useGosvaraSheet() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();


  // New: Get Gosvara Data Query
  const useGosvaraData = (schoolId: number, classId: number, sessionId: number, medium: string, enabled = true): UseQueryResult<GosvaraResponseDto[]> =>
    useQuery({
      queryKey: ['gosvaraData', schoolId, classId, sessionId, medium],
      queryFn: () => getGosvaraByClassAndSession({ schoolId, classId, sessionId, medium }),
      enabled,
    });

  // Get all gosvara data for enhanced table
  const useAllGosvaraData = (
    schoolId: number,
    sessionId: number,
    medium: string,
    classId?: number,
    className?: string,
    enabled = true
  ): UseQueryResult<GosvaraResponseDto[]> =>
    useQuery({
      queryKey: ['allGosvaraData', schoolId, sessionId, medium, classId ?? null, className ?? null],
      queryFn: () => getGosvaraByClassAndSession({ schoolId, sessionId, medium, classId, className }),
      enabled,
    });

  // New: Monthly Gosvara Query
  const useMonthlyGosvara = (payload: GosvaraGetRequest, enabled = true): UseQueryResult<GosvaraGetResponse> =>
    useQuery({
      queryKey: ['monthlyGosvara', payload],
      queryFn: () => getMonthlyGosvara(payload),
      enabled,
    });

  // Category-wise Gosvara Query
  const useCategoryWiseGosvara = (
    payload: GosvaraRequestDto,
    medium: string = 'ALL',
    enabled = true
  ): UseQueryResult<Record<string, GosvaraResponseDto>> =>
    useQuery({
      queryKey: ['categoryWiseGosvara', payload, medium],
      queryFn: () => getCategoryWiseGosvara(payload, medium),
      enabled,
    });

  return {


    useGosvaraData,
    useAllGosvaraData,
    useMonthlyGosvara,
    useCategoryWiseGosvara,
  };
}

